import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENT
import { Divider, Button, Tooltip, Spin, notification, Alert } from "antd";
// DEVICE DETECT
import { isMobile } from "react-device-detect";
// COPY TO CLIBBOARD
import { CopyToClipboard } from "react-copy-to-clipboard";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRYARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faCopy,
	faEye,
	faShield,
	faWarning,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
	udpateOpenModalTotpToken,
	updateTotpTokenIsValid,
} from "../../redux/authentication/authenticationSlice";
import { updateUserAlert } from "../../redux/users/userSlice";
import {
	usePostMeOfflinePSWMutation,
	useLazyGetMeOfflinePSWQuery,
} from "../../redux/login/loginAPI";

// COMPONENTS
import AuthenticationTOTP from "../authentication/AuthenticationTOTP";
// AUTHORIZATION
import GETJwtToken from "../../redux/authentication/authentication";

function OfflinePsw() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const [t] = useTranslation("global");
	const dispatch = useDispatch();

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [isLoading, setIsLoading] = useState(false);
	const [offlinePsw, setOfflinePsw] = useState("***************");
	const [createdOfflinePsw, setCreatedOfflinePsw] = useState(false);
	const [resetPassword, setResetPassword] = useState(false);
	// eslint-disable-next-line no-unused-vars
	const [copied, setCopied] = useState(false);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme, openDrawerUser, userLogged } = useSelector(
		(state: any) => state.home
	);
	const { totpTokenIsValid } = useSelector(
		(state: any) => state.authentication
	);
	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		triggerPostMeOfflinePsw,
		{
			data: dataPostOfflinePassword,
			isLoading: isLoadingPostOfflinePassword,
			isSuccess: isSuccessPostOfflinePassword,
			isError: isErrorPostOfflinePassword,
			error: errorPostOfflinePassword,
		},
	] = usePostMeOfflinePSWMutation();
	const [
		triggerGetMeOfflinePsw,
		{ data: dataGetOfflinePassword, isLoading: isLoadingGetOfflinePassword },
	] = useLazyGetMeOfflinePSWQuery();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const showIsLoading = () => {
		setIsLoading(true);
		setTimeout(() => {
			setIsLoading(false);
		}, 500);
	};

	const handleOpenModalTotpToken = () => {
		dispatch(udpateOpenModalTotpToken(true));
	};

	const getOfflinePassword = async () => {
		const token = await GETJwtToken();
		if (createdOfflinePsw && !resetPassword) {
			triggerGetMeOfflinePsw({ token });
		} else {
			triggerPostMeOfflinePsw({ token });
		}
	};

	const onCopy = () => {
		setCopied(true);
		notification.open({
			message: `${t("general.copyToClipboard")}`,
			description: `${t("general.passwordCopiedToClipboard")}`,
			placement: "topLeft",
			className: `monitoring__notification__${theme}`,
		});
	};
	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		showIsLoading();
		setResetPassword(false);
	}, [openDrawerUser]);

	useEffect(() => {
		setResetPassword(false);
		if (totpTokenIsValid && openDrawerUser) {
			getOfflinePassword();
			setTimeout(() => {
				dispatch(updateTotpTokenIsValid(false));
			}, 1000);
		}
	}, [totpTokenIsValid]);

	useEffect(() => {
		if (dataPostOfflinePassword && dataPostOfflinePassword.offline_password) {
			setOfflinePsw(dataPostOfflinePassword.offline_password);
		}
	}, [dataPostOfflinePassword]);

	useEffect(() => {
		if (dataGetOfflinePassword && dataGetOfflinePassword.offline_password) {
			setOfflinePsw(dataGetOfflinePassword.offline_password);
		}
	}, [dataGetOfflinePassword]);

	useEffect(() => {
		if (userLogged) {
			setCreatedOfflinePsw(userLogged.created_offline_psw);
		}
	}, [userLogged]);

	useEffect(() => {
		if (isSuccessPostOfflinePassword) {
			setTimeout(() => {
				dispatch(
					updateUserAlert({
						title: "Success",
						description: t("userManagement.passwordResetSuccessfully"),
						status: "success",
					})
				);
			}, 150);
			dispatch(updateUserAlert(false));
		}
		if (isErrorPostOfflinePassword) {
			setTimeout(() => {
				dispatch(
					updateUserAlert({
						title: "Error",
						description: errorPostOfflinePassword,
						status: "error",
					})
				);
			}, 150);
			dispatch(updateUserAlert(false));
		}
	}, [isSuccessPostOfflinePassword, isErrorPostOfflinePassword]);
	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Spin
			spinning={
				isLoadingGetOfflinePassword || isLoadingPostOfflinePassword || isLoading
			}
		>
			<div>
				{/** ************************* */}
				{/** BODY */}
				{/** ************************************************* */}
				<div>
					<Divider orientation='left' className='generalStyles__drawerDivider'>
						<h5>
							<FontAwesomeIcon
								icon={faShield}
								className='generalStyles__info generalStyles__mrFix'
							/>
							{t("general.offlineAuthentication")}
						</h5>
					</Divider>
				</div>
				<div className={`drawer__box__${theme} row`}>
					{/* OFFLINE PSW */}
					<div className='generalStyles__flex'>
						<div>
							<p
								className={isMobile ? "drawer__titleMobile" : "drawer__title"}
								style={{ marginTop: "8px" }}
							>
								{t("general.offlinePassword")}
							</p>
						</div>
						<div className='drawer__textContainer generalStyles__flex'>
							<div>
								<p style={{ marginTop: "8px" }}>{offlinePsw}</p>
							</div>
							<div className='generalStyles__mlFix25'>
								{offlinePsw === "***************" ? (
									<Tooltip
										placement='top'
										title={t("general.offlineGeneratePassword")}
									>
										<Button
											className='buttonStyle__1'
											shape='circle'
											onClick={() => {
												handleOpenModalTotpToken();
												setResetPassword(false);
											}}
											icon={<FontAwesomeIcon icon={faEye} />}
										/>{" "}
									</Tooltip>
								) : (
									<CopyToClipboard onCopy={onCopy} text={offlinePsw}>
										<Tooltip
											placement='top'
											title={t("general.copyToClipboard")}
										>
											<Button
												className='buttonStyle__1'
												shape='circle'
												icon={<FontAwesomeIcon icon={faCopy} />}
											/>{" "}
										</Tooltip>
									</CopyToClipboard>
								)}
							</div>
						</div>
					</div>
					{resetPassword && (
						<Alert
							className='mb-3'
							message='Warning'
							description={t("userManagement.alertResetPassword")}
							type='warning'
							showIcon
							closable
						/>
					)}
					{/* RESET PASSWORD */}
					<div className='generalStyles__flex'>
						<div>
							<p
								className={isMobile ? "drawer__titleMobile" : "drawer__title"}
								style={{ marginTop: "8px" }}
							>
								{t("userManagement.generateNewPassword")}
							</p>
						</div>
						<div className='drawer__textContainer generalStyles__flex'>
							<div>
								<Tooltip
									placement='top'
									title={t("userManagement.generateNewPassword")}
								>
									{resetPassword ? (
										<Button
											className='buttonStyle__4 '
											onClick={() => {
												showIsLoading();
												handleOpenModalTotpToken();
											}}
											icon={
												<FontAwesomeIcon
													className='generalStyles__mrFix'
													icon={faWarning}
												/>
											}
										>
											{t("general.confirm")}
										</Button>
									) : (
										<Button
											className='buttonStyle__4 '
											onClick={() => {
												showIsLoading();
												setResetPassword(true);
											}}
											icon={
												<FontAwesomeIcon
													className='generalStyles__mrFix'
													icon={faWarning}
												/>
											}
										>
											{t("general.reset")}
										</Button>
									)}
								</Tooltip>
							</div>
						</div>
					</div>
				</div>
				{/** AUTHENTICATION TOTP CODE */}
				<AuthenticationTOTP />
			</div>
		</Spin>
	);
}

export default OfflinePsw;
